import { URL_CATEGORY_NONE } from '@client/models/router.model';
import { collectProductListSelectableIds } from '@features/products/product-list-build.helpers';
import { PRODUCT_ID_SEPARATOR } from '@features/products/product-list.helpers';
import { getResolvedBuildingVersion } from '@shared/helpers/actions/resolved-version.helpers';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';
import { isElementActiveInProposal } from '@shared/helpers/proposal.helpers';
import { getProposal } from '@shared/helpers/proposals/proposals.helpers';
import {
  getElementById,
  getPathAndElementById,
  isDeletedElement,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import { getResultsRecord } from '@shared/helpers/results.helpers';
import type { ElementListUrlPage } from '@client/hooks/use-router.hooks';
import type { Baseline } from '@shared/models/project.model';
import type { ProposalId } from '@shared/models/proposals.model';

type ProposalNavigationNavigateTo = (params: {
  page: ElementListUrlPage;
  elementId: string;
}) => void | Promise<void>;

/**
 * True when the given id refers to an actual element row selection,
 * not {@link URL_CATEGORY_NONE}.
 */
export const hasElementSelection = (
  selectedElementId: string | undefined,
): selectedElementId is string =>
  Boolean(selectedElementId && selectedElementId !== URL_CATEGORY_NONE);

/**
 * Checks whether selecting {@link targetProposalId} should clear the selected
 * element. Uses the proposal-resolved tree (the navigation target). Clears when
 * the row is missing, inactive, or soft-deleted — matching element-list visibility.
 */
export const shouldDeselectElementForProposalSelection = (
  version: Baseline | undefined,
  targetProposalId: ProposalId | undefined,
  selectedElementId: string | undefined,
): boolean => {
  if (!version || !hasElementSelection(selectedElementId)) return false;

  const elementId = selectedElementId;

  const resolvedVersion = getResolvedBuildingVersion(version, targetProposalId);
  const proposal = targetProposalId
    ? getProposal(version, targetProposalId)
    : undefined;
  const { element: selectedElement, path } = getPathAndElementById(
    resolvedVersion,
    elementId,
  );

  // Product-list group/category ids are not tree nodes — handled by
  // {@link shouldClearStaleProductListSelection}.
  if (!projectGuards.is.element(selectedElement)) return false;

  if (!isElementActiveInProposal(proposal, path, selectedElement)) return true;

  return [...path, selectedElement].some(isDeletedElement);
};

/**
 * True when the URL selection is not present in the product list built for the
 * navigation target (baseline or proposal). Keeps valid group/item rows; clears
 * stale product-list ids the same way the element list drops missing rows.
 */
export const shouldClearStaleProductListSelection = (
  version: Baseline | undefined,
  targetProposalId: ProposalId | undefined,
  selectedElementId: string | undefined,
): boolean => {
  if (
    !version ||
    !selectedElementId ||
    selectedElementId === URL_CATEGORY_NONE
  ) {
    return false;
  }

  const elementId = selectedElementId;

  const resolvedVersion = getResolvedBuildingVersion(version, targetProposalId);
  const proposal = targetProposalId
    ? getProposal(version, targetProposalId)
    : undefined;
  const resultsRecord = proposal
    ? getResultsRecord(version, proposal)
    : (version.resultsRecord ?? {});

  const selectableIds = collectProductListSelectableIds(
    resolvedVersion,
    resultsRecord,
  );
  if (selectableIds.has(elementId)) return false;

  const selectedInResolvedTree = getElementById(
    resolvedVersion,
    elementId,
    false,
  );

  // Product-list group / category synthetic ids
  if (!projectGuards.is.element(selectedInResolvedTree)) return true;

  // Product element row on the products list
  if (isProductElement(selectedInResolvedTree)) return true;

  return false;
};

/**
 * Generic product id encoded in a product-list group row id (`epdId__genericId` or `genericId`).
 */
const getProductListGroupGenericId = (selectionId: string): string => {
  const separatorIndex = selectionId.indexOf(PRODUCT_ID_SEPARATOR);
  if (separatorIndex < 0) return selectionId;
  return selectionId.slice(separatorIndex + PRODUCT_ID_SEPARATOR.length);
};

/**
 * When a product-list group id changes between baseline and proposal (EPD applied or cleared),
 * map the stale id to the equivalent row in the target list.
 */
const findRemappedProductListSelectionId = (
  selectableIds: Set<string>,
  staleId: string,
): string | undefined => {
  if (selectableIds.has(staleId)) return staleId;

  const genericId = getProductListGroupGenericId(staleId);
  if (selectableIds.has(genericId)) return genericId;

  return Array.from(selectableIds).find(
    (id) =>
      id === genericId || id.endsWith(`${PRODUCT_ID_SEPARATOR}${genericId}`),
  );
};

export type ProposalNavigationSelectionResolution =
  { type: 'keep' } | { type: 'clear' } | { type: 'remap'; elementId: string };

/**
 * Resolves URL element selection when switching baseline/proposal: keep, clear, or remap
 * product-list group rows whose id changes with EPD state (DEV-3315, DEV-3366).
 */
export const resolveElementSelectionForProposalNavigation = (
  version: Baseline | undefined,
  targetProposalId: ProposalId | undefined,
  selectedElementId: string | undefined,
): ProposalNavigationSelectionResolution => {
  if (!version || !hasElementSelection(selectedElementId))
    return { type: 'keep' };

  const elementId = selectedElementId;

  if (
    shouldDeselectElementForProposalSelection(
      version,
      targetProposalId,
      elementId,
    )
  ) {
    return { type: 'clear' };
  }

  const resolvedVersion = getResolvedBuildingVersion(version, targetProposalId);
  const proposal = targetProposalId
    ? getProposal(version, targetProposalId)
    : undefined;
  const resultsRecord = proposal
    ? getResultsRecord(version, proposal)
    : (version.resultsRecord ?? {});

  const selectableIds = collectProductListSelectableIds(
    resolvedVersion,
    resultsRecord,
  );

  if (selectableIds.has(elementId)) return { type: 'keep' };

  const remappedId = findRemappedProductListSelectionId(
    selectableIds,
    elementId,
  );
  if (remappedId) return { type: 'remap', elementId: remappedId };

  if (
    shouldClearStaleProductListSelection(version, targetProposalId, elementId)
  ) {
    return { type: 'clear' };
  }

  return { type: 'keep' };
};

/**
 * Navigates list selection after baseline/proposal change when resolution is not `keep`.
 */
export const applyElementSelectionForProposalNavigation = (
  resolution: ProposalNavigationSelectionResolution,
  navigateTo: ProposalNavigationNavigateTo,
  page: ElementListUrlPage,
): void => {
  if (resolution.type === 'keep') return;
  if (resolution.type === 'clear') {
    void navigateTo({ page, elementId: URL_CATEGORY_NONE });
    return;
  }
  void navigateTo({ page, elementId: resolution.elementId });
};

/**
 * Whether proposal/baseline navigation should clear the current element selection
 * before applying the new proposal overlay.
 */
export const shouldClearElementSelectionForProposalNavigation = (
  version: Baseline | undefined,
  targetProposalId: ProposalId | undefined,
  selectedElementId: string | undefined,
): boolean =>
  shouldDeselectElementForProposalSelection(
    version,
    targetProposalId,
    selectedElementId,
  ) ||
  shouldClearStaleProductListSelection(
    version,
    targetProposalId,
    selectedElementId,
  );
